import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "";

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
});

export interface DailyMealRecord {
  _id: string;
  sharedStockGroupId?: string | null;
  operatingDate: string;
  itemType?: "NowMeal";
  mealId: {
    _id: string;
    name: string;
    image: string;
    categoryId?: { _id: string; name: string };
    currentPrice: number;
    mrp: number;
    isAddOn?: boolean;
    isActive?: boolean;
  };
  kitchenId?: { _id: string; name: string } | null;
  source: "RULE" | "MANUAL_ADDITION" | "SWAP_SET";
  swapSetSerial?: number | null;
  appliedRuleId?: { _id: string; ruleName?: string; scheduleType?: string } | null;
  stockMode: "LIMITED" | "UNLIMITED";
  ruleDerivedQuantity: number;
  finalConfiguredQuantity: number;
  soldQuantity: number;
  remainingQuantity: number | null;
  isAvailable: boolean;
  isManuallyOverridden: boolean;
  isPoolStockOverridden?: boolean;
  overrideReason?: string;
  isProjectedOnly?: boolean;
  reason?: string;
}

export interface UnavailableConstituent {
  mealId: string;
  name?: string;
  reason?: string;
}

export interface ComboConstituentItem {
  instantMealId: {
    _id: string;
    name: string;
    image?: string;
    currentPrice?: number;
    isAddOn?: boolean;
    isActive?: boolean;
  };
  quantity: number;
}

export interface DailyComboRecord {
  _id: string;
  operatingDate: string;
  itemType?: "NowCombo";
  mealId: {
    _id: string;
    title: string;
    description?: string;
    image?: string;
    originalPrice?: number;
    comboPrice?: number;
    items?: ComboConstituentItem[];
  };
  kitchenId?: { _id: string; name: string } | null;
  source: "RULE" | "MANUAL_ADDITION" | "SWAP_SET";
  swapSetSerial?: number | null;
  appliedRuleId?: { _id: string; ruleName?: string; scheduleType?: string } | null;
  stockMode: "LIMITED" | "UNLIMITED";
  ruleDerivedQuantity: number;
  finalConfiguredQuantity: number;
  soldQuantity: number;
  remainingQuantity: number | null;
  isAvailable: boolean;
  isConstituentAvailable?: boolean;
  unavailableConstituents?: UnavailableConstituent[];
  calculatedConstituentCapacity?: number | null;
  isManuallyOverridden: boolean;
  overrideReason?: string;
  isProjectedOnly?: boolean;
  reason?: string;
}

export interface MenuSideItem {
  _id: string;
  name: string;
  price: number;
  category: "drink" | "dip" | "dressing" | "other";
  visibleInMenu: boolean;
  available: boolean;
}

export interface DailyAvailabilitySummary {
  totalMeals: number;
  activeAvailableMeals: number;
  totalCombos?: number;
  activeAvailableCombos?: number;
  totalSellableUnits: number;
  soldUnits: number;
  outOfStockCount: number;
  manualOverrideCount: number;
}

export interface DailyAvailabilityResponse {
  sharedStockGroups?: Array<{ _id: string; name: string; description?: string }>;
  success: boolean;
  status: "GENERATED" | "MODIFIED" | "PROJECTED_ESTIMATE" | "UNAVAILABLE";
  isProjected: boolean;
  operatingDate: string;
  isToday: boolean;
  summary: DailyAvailabilitySummary;
  records: DailyMealRecord[];
  combos?: DailyComboRecord[];
  menuSides?: MenuSideItem[];
}

export const dailyAvailabilityApi = {
  getDaily: async (date?: string, kitchenId?: string): Promise<DailyAvailabilityResponse> => {
    let url = `${API_URL}/api/admin/daily-availability?`;
    if (date) url += `&date=${encodeURIComponent(date)}`;
    if (kitchenId) url += `&kitchenId=${encodeURIComponent(kitchenId)}`;

    const response = await axios.get(url, { headers: getAuthHeaders() });
    return response.data;
  },

  generate: async (date?: string, forceResetOverrides: boolean = false, kitchenId?: string) => {
    const response = await axios.post(
      `${API_URL}/api/admin/daily-availability/generate`,
      { date, forceResetOverrides, kitchenId },
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  adjustQuantity: async (id: string, quantity: number, reason?: string, stockMode?: "LIMITED" | "UNLIMITED", quantityMode?: "CAPACITY" | "REMAINING") => {
    const response = await axios.patch(
      `${API_URL}/api/admin/daily-availability/${id}/quantity`,
      { quantity, reason, stockMode, quantityMode },
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  toggleAvailability: async (id: string, isAvailable: boolean, reason?: string) => {
    const response = await axios.patch(
      `${API_URL}/api/admin/daily-availability/${id}/toggle`,
      { isAvailable, reason },
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  toggleComboAvailability: async (comboId: string, isAvailable: boolean, reason?: string, date?: string) => {
    const response = await axios.patch(
      `${API_URL}/api/admin/daily-availability/combos/${comboId}/toggle`,
      { isAvailable, reason, date },
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  toggleSideAvailability: async (sideId: string, isAvailable: boolean, kitchenId?: string) => {
    const response = await axios.patch(
      `${API_URL}/api/admin/daily-availability/sides/${sideId}/toggle`,
      { isAvailable, ...(kitchenId ? { kitchenId } : {}) },
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  addManualMeal: async (data: {
    date: string;
    mealId: string;
    quantity: number;
    stockMode: "LIMITED" | "UNLIMITED";
    reason?: string;
    kitchenId?: string;
  }) => {
    const response = await axios.post(`${API_URL}/api/admin/daily-availability/manual-add`, data, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  revertOverride: async (id: string) => {
    const response = await axios.post(
      `${API_URL}/api/admin/daily-availability/${id}/revert`,
      {},
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  getLogs: async (date?: string) => {
    let url = `${API_URL}/api/admin/daily-availability/logs?`;
    if (date) url += `&date=${encodeURIComponent(date)}`;

    const response = await axios.get(url, { headers: getAuthHeaders() });
    return response.data;
  },
};
